import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const CATEGORIAS = {
  insumos:      { label: 'Insumos',      icon: '📦' },
  ingredientes: { label: 'Ingredientes', icon: '🧪' },
  empaques:     { label: 'Empaques',     icon: '🛍️' },
  aseo:         { label: 'Aseo',         icon: '🧹' },
  papeleria:    { label: 'Papelería',    icon: '📄' },
  utensilios:   { label: 'Utensilios',   icon: '🔧' },
};

const SINNO = ['SI/NO hay repuesto','SI/NO buen estado','SI/NO HAY REPUESTO','SI/NO BUEN ESTADO'];
const esSiNo = (unidad) => SINNO.some(s => unidad?.toUpperCase().includes('SI/NO'));

function InputCantidad({ valor, onChange, unidad, disabled, placeholder }) {
  if (esSiNo(unidad)) {
    return (
      <div style={{ display:'flex', gap:6 }}>
        {['SI','NO'].map(op => (
          <button key={op} disabled={disabled} onClick={() => !disabled && onChange(op)}
            style={{ flex:1, padding:'7px 0', border:'2px solid', borderColor: valor===op ? (op==='SI'?'#10B981':'#EF4444') : '#E5E7EB',
              borderRadius:8, fontWeight:700, fontSize:13, cursor:disabled?'default':'pointer',
              background: valor===op ? (op==='SI'?'#D1FAE5':'#FEE2E2') : 'white',
              color: valor===op ? (op==='SI'?'#065F46':'#991B1B') : '#9CA3AF' }}>
            {op}
          </button>
        ))}
      </div>
    );
  }
  return (
    <input type="number" min="0" value={valor ?? ''} disabled={disabled}
      onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      placeholder={placeholder || '0'}
      style={{ width:'100%', padding:'7px 10px', border:'2px solid #E5E7EB', borderRadius:8,
        fontSize:14, outline:'none', boxSizing:'border-box', textAlign:'center',
        background: disabled ? '#F9FAFB' : 'white', color: disabled ? '#9CA3AF' : '#111827' }} />
  );
}

export default function InventarioGeneral() {
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'gerencia' || usuario?.rol === 'administracion' || usuario?.rol === 'produccion';
  const esPDV   = usuario?.rol === 'punto_venta';

  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [tipo, setTipo]           = useState('diario');
  const [fecha, setFecha]         = useState(hoy);
  const [bodegas, setBodegas]     = useState([]);
  const [bodegaId, setBodegaId]   = useState('');
  const [items, setItems]         = useState([]);
  const [valores, setValores]     = useState({});
  const [registro, setRegistro]   = useState(null);
  const [cargando, setCargando]   = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [vistaTab, setVistaTab]   = useState('formulario');
  const [registroDetalle, setRegistroDetalle] = useState(null);
  const [compras, setCompras]     = useState([]);
  const [comprasHoy, setComprasHoy] = useState([]);
  const [guardandoCompra, setGuardandoCompra] = useState(false);
  const [nuevaCompra, setNuevaCompra] = useState({ item_id: '', cantidad: '', valor_unitario: '', proveedor: '', notas: '' });

  // Detectar si es PA (Parque Alegra)
  const bodegaActual = bodegas.find(b => String(b.id) === String(bodegaId));
  const esPA = bodegaActual?.nombre?.toUpperCase().includes('PARQUE') || bodegaActual?.nombre?.toUpperCase().includes('ALEGRA') || false;

  const estadoColor = {
    borrador:  { bg:'#FEF3C7', color:'#92400E', label:'Borrador' },
    enviado:   { bg:'#D1FAE5', color:'#065F46', label:'Enviado ✓' },
    corregido: { bg:'#DBEAFE', color:'#1E40AF', label:'Corregido' },
  };

  // Para punto_venta, auto-asignar su bodega
  useEffect(() => {
    API.get('/bodegas').then(r => {
      const pvs = r.data.filter(b => b.tipo === 'punto_venta' || !b.tipo);
      setBodegas(pvs);
      if (esPDV && usuario.bodega_id) {
        setBodegaId(String(usuario.bodega_id));
      } else if (esAdmin && pvs.length) {
        setBodegaId(String(pvs[0].id));
      }
    }).catch(() => {});
  }, []);

  // Cargar items según tipo y bodega
  useEffect(() => {
    if (!bodegaId) return;
    API.get('/inventario/items', { params: { tipo, es_pa: esPA } })
      .then(r => setItems(r.data))
      .catch(() => {});
  }, [tipo, bodegaId, esPA]);

  // Cargar registro existente
  const cargarRegistro = useCallback(() => {
    if (!bodegaId || !fecha) return;
    setCargando(true);
    API.get('/inventario/registro', { params: { bodega_id: bodegaId, tipo, fecha } })
      .then(r => {
        setRegistro(r.data);
        if (r.data?.detalle) {
          const v = {};
          r.data.detalle.forEach(d => {
            v[d.item_id] = { cantidad: d.cantidad, cortesia: d.cortesia, danados: d.danados, observacion: d.observacion };
          });
          setValores(v);
        } else {
          setValores({});
        }
      }).catch(() => { setRegistro(null); setValores({}); })
      .finally(() => setCargando(false));
  }, [bodegaId, tipo, fecha]);

  useEffect(() => { cargarRegistro(); }, [cargarRegistro]);

  // Cargar historial
  useEffect(() => {
    if (!bodegaId) return;
    API.get('/inventario/historial', { params: { bodega_id: bodegaId, tipo, limit: 10 } })
      .then(r => setHistorial(r.data)).catch(() => {});
  }, [bodegaId, tipo, registro]);

  const verRegistro = async (registro) => {
    try {
      const r = await API.get('/inventario/registro', { params: { bodega_id: registro.bodega_id || bodegaId, tipo: registro.tipo, fecha: registro.fecha.slice(0,10) } });
      // Use names from endpoint directly (item_nombre, categoria from JOIN)
      const detalle = (r.data?.detalle || []).map(d => ({
        ...d,
        nombre: d.item_nombre || d.nombre || d.item_id,
        categoria: d.categoria || '—',
      }));
      setRegistroDetalle({ ...registro, detalle });
    } catch { toast.error('Error cargando detalle'); }
  };

  const setValor = (itemId, campo, val) => {
    setValores(prev => ({ ...prev, [itemId]: { ...(prev[itemId]||{}), [campo]: val } }));
  };

  const bloqueado = registro?.estado === 'enviado' && !esAdmin;

  const handleGuardar = async (enviar = false) => {
    if (!bodegaId) { toast.error('Selecciona un punto de venta'); return; }
    setGuardando(true);
    try {
      const payload = items.map(item => ({
        item_id:     item.id,
        cantidad:    valores[item.id]?.cantidad ?? null,
        cortesia:    valores[item.id]?.cortesia ?? 0,
        danados:     valores[item.id]?.danados  ?? 0,
        observacion: valores[item.id]?.observacion ?? null,
      }));
      await API.post('/inventario/guardar', { bodega_id: bodegaId, tipo, fecha, items: payload, enviar });
      toast.success(enviar ? '✅ Inventario enviado y bloqueado' : '💾 Guardado como borrador');
      cargarRegistro();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const handleCorregir = async () => {
    try {
      await API.post('/inventario/corregir', { registro_id: registro.id });
      toast.success('Inventario desbloqueado para corrección');
      cargarRegistro();
    } catch { toast.error('Error'); }
  };

  // Agrupar items por categoría
  const porCategoria = {};
  items.forEach(item => {
    if (!porCategoria[item.categoria]) porCategoria[item.categoria] = [];
    porCategoria[item.categoria].push(item);
  });

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", maxWidth:900, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#111827' }}>📦 Inventario General</h1>
          <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:14 }}>Diario al cierre · Semanal los lunes al abrir</p>
        </div>
        {registro?.estado && (
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ padding:'6px 14px', borderRadius:20, fontWeight:700, fontSize:13,
              background: estadoColor[registro.estado]?.bg, color: estadoColor[registro.estado]?.color }}>
              {estadoColor[registro.estado]?.label}
            </span>
            {registro.estado === 'enviado' && esAdmin && (
              <button onClick={handleCorregir} style={{ padding:'6px 14px', border:'2px solid #F59E0B', borderRadius:8, background:'white', cursor:'pointer', fontSize:12, fontWeight:700, color:'#92400E' }}>
                ✏️ Permitir corrección
              </button>
            )}
          </div>
        )}
      </div>

      {/* Controles */}
      <div style={{ background:'white', borderRadius:14, padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:18, display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
        {/* Tipo */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Tipo</div>
          <div style={{ display:'flex', background:'#F3F4F6', borderRadius:8, padding:2 }}>
            {[{id:'diario',label:'📅 Diario'},{id:'semanal',label:'📆 Semanal'}].map(t => (
              <button key={t.id} onClick={() => setTipo(t.id)} style={{ padding:'6px 14px', border:'none', borderRadius:7, cursor:'pointer', fontSize:13,
                fontWeight: tipo===t.id?700:500, background: tipo===t.id?'white':'transparent', color: tipo===t.id?'#111827':'#6B7280',
                boxShadow: tipo===t.id?'0 1px 3px rgba(0,0,0,0.1)':'none' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {/* Bodega */}
        {esAdmin && (
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Punto de venta</div>
            <select value={bodegaId} onChange={e => setBodegaId(e.target.value)}
              style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
              {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </div>
        )}
        {/* Fecha */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Fecha</div>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} max={hoy}
            style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        {/* Tabs */}
        <div style={{ marginLeft:'auto' }}>
          <div style={{ display:'flex', background:'#F3F4F6', borderRadius:8, padding:2 }}>
            {[{id:'formulario',label:'📝 Llenar'},{id:'historial',label:'📋 Historial'}, ...(esPDV ? [{id:'compras',label:'🛒 Compras directas'}] : [])].map(t => (
              <button key={t.id} onClick={() => setVistaTab(t.id)} style={{ padding:'6px 14px', border:'none', borderRadius:7, cursor:'pointer', fontSize:13,
                fontWeight: vistaTab===t.id?700:500, background: vistaTab===t.id?'white':'transparent', color: vistaTab===t.id?'#111827':'#6B7280',
                boxShadow: vistaTab===t.id?'0 1px 3px rgba(0,0,0,0.1)':'none' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {vistaTab === 'historial' ? (
        /* ── HISTORIAL ── */
        registroDetalle ? (
          /* Vista detalle de un registro */
          <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ background:'#1a1a2e', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ color:'white', fontWeight:800, fontSize:15 }}>
                  📦 {registroDetalle.tipo === 'diario' ? 'Inventario Diario' : 'Inventario Semanal'} — {registroDetalle.bodega_nombre || bodegaActual?.nombre}
                </div>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginTop:2 }}>
                  {registroDetalle.fecha ? (() => { try { return format(new Date(registroDetalle.fecha.slice(0,10)+'T12:00'), "d 'de' MMMM yyyy", {locale:es}); } catch { return registroDetalle.fecha; } })() : ''}
                  {' · '}{registroDetalle.detalle?.length || 0} ítems registrados
                </div>
              </div>
              <button onClick={() => setRegistroDetalle(null)}
                style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                ← Volver
              </button>
            </div>
            {registroDetalle.detalle?.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Sin detalle registrado</div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      {['Ítem','Categoría','Cantidad','Cortesía','Dañados','Observación'].map(h => (
                        <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {registroDetalle.detalle.map((d, i) => {
                      return (
                        <tr key={d.id} style={{ borderTop:'1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                          <td style={{ padding:'10px 14px', fontWeight:600 }}>{d.nombre || d.item_id}</td>
                          <td style={{ padding:'10px 14px', color:'#6B7280', fontSize:11 }}>{d.categoria || '—'}</td>
                          <td style={{ padding:'10px 14px', fontWeight:700, color:'#111827' }}>{d.cantidad ?? '—'}</td>
                          <td style={{ padding:'10px 14px', color: d.cortesia > 0 ? '#10B981' : '#9CA3AF' }}>{d.cortesia || '—'}</td>
                          <td style={{ padding:'10px 14px', color: d.danados > 0 ? '#EF4444' : '#9CA3AF' }}>{d.danados || '—'}</td>
                          <td style={{ padding:'10px 14px', color:'#6B7280', fontSize:11 }}>{d.observacion || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
        <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', fontWeight:800, fontSize:15 }}>📋 Últimos registros</div>
          {historial.length === 0 ? (
            <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Sin registros aún</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['Fecha','Punto','Tipo','Estado','Enviado por',''].map(h => (
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historial.map((h, i) => {
                  const ec = estadoColor[h.estado] || estadoColor.borrador;
                  return (
                    <tr key={h.id} style={{ borderTop:'1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                      <td style={{ padding:'10px 14px', fontWeight:700 }}>{(() => { try { const d = new Date(h.fecha+'T12:00'); return isNaN(d) ? h.fecha || '—' : format(d,"d 'de' MMM yyyy",{locale:es}); } catch { return h.fecha || '—'; } })()}</td>
                      <td style={{ padding:'10px 14px' }}>{h.bodega_nombre}</td>
                      <td style={{ padding:'10px 14px' }}><span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'#EEF2FF', color:'#4338CA', fontWeight:700 }}>{h.tipo}</span></td>
                      <td style={{ padding:'10px 14px' }}><span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:ec.bg, color:ec.color, fontWeight:700 }}>{ec.label}</span></td>
                      <td style={{ padding:'10px 14px', color:'#6B7280' }}>{h.enviado_por_nombre || '—'}</td>
                      <td style={{ padding:'10px 14px' }}>
                        <button onClick={() => verRegistro(h)}
                          style={{ padding:'4px 12px', border:'1px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                          Ver registro
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )
      ) : cargando ? (
        <div style={{ padding:48, textAlign:'center', color:'#9CA3AF' }}>Cargando...</div>
      ) : (
        /* ── FORMULARIO ── */
        <>
          {bloqueado && (
            <div style={{ background:'#D1FAE5', border:'1px solid #6EE7B7', borderRadius:10, padding:'12px 18px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>
              ✅ Este inventario ya fue enviado el {registro.enviado_at ? format(new Date(registro.enviado_at),'d/M/yyyy HH:mm') : ''} por {registro.enviado_por_nombre}. Solo gerencia puede desbloquearlo.
            </div>
          )}

          {Object.entries(porCategoria).map(([cat, catItems]) => {
            const catInfo = CATEGORIAS[cat] || { label: cat, icon: '📋' };
            return (
              <div key={cat} style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:16 }}>
                {/* Header categoría */}
                <div style={{ background:'#1a1a2e', padding:'10px 18px', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:16 }}>{catInfo.icon}</span>
                  <span style={{ color:'white', fontWeight:800, fontSize:14, textTransform:'uppercase', letterSpacing:'0.04em' }}>{catInfo.label}</span>
                  <span style={{ marginLeft:'auto', fontSize:11, color:'rgba(255,255,255,0.5)' }}>{catItems.length} items</span>
                </div>

                {/* Header columnas */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 90px 90px', gap:8, padding:'8px 18px', background:'#F9FAFB', borderBottom:'1px solid #F3F4F6' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Item</div>
                  {tipo === 'diario' ? (
                    <>
                      <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', textAlign:'center' }}>Inventario hoy</div>
                      <div style={{ fontSize:11, fontWeight:700, color:'#F59E0B', textTransform:'uppercase', textAlign:'center' }}>Cortesía</div>
                      <div style={{ fontSize:11, fontWeight:700, color:'#EF4444', textTransform:'uppercase', textAlign:'center' }}>Dañados</div>
                    </>
                  ) : (
                    <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', textAlign:'center', gridColumn:'span 3' }}>Cantidad</div>
                  )}
                </div>

                {/* Items */}
                {catItems.map((item, idx) => (
                  <div key={item.id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 90px 90px', gap:8, padding:'10px 18px', borderBottom:'1px solid #F9FAFB', background: idx%2===0?'white':'#FAFAFA', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{item.nombre}</div>
                      <div style={{ fontSize:11, color:'#9CA3AF' }}>{item.unidad_medida}</div>
                    </div>
                    {tipo === 'diario' ? (
                      <>
                        <InputCantidad valor={valores[item.id]?.cantidad} unidad={item.unidad_medida}
                          disabled={bloqueado} onChange={v => setValor(item.id,'cantidad',v)} />
                        <InputCantidad valor={valores[item.id]?.cortesia} unidad="numero"
                          disabled={bloqueado} placeholder="0"
                          onChange={v => setValor(item.id,'cortesia',v)} />
                        <InputCantidad valor={valores[item.id]?.danados} unidad="numero"
                          disabled={bloqueado} placeholder="0"
                          onChange={v => setValor(item.id,'danados',v)} />
                      </>
                    ) : (
                      <div style={{ gridColumn:'span 3' }}>
                        <InputCantidad valor={valores[item.id]?.cantidad} unidad={item.unidad_medida}
                          disabled={bloqueado} onChange={v => setValor(item.id,'cantidad',v)} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Botones acción */}
          {!bloqueado && (
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8, marginBottom:24 }}>
              <button onClick={() => handleGuardar(false)} disabled={guardando}
                style={{ padding:'11px 24px', border:'2px solid #E5E7EB', borderRadius:10, background:'white', cursor:'pointer', fontSize:14, fontWeight:700, color:'#374151' }}>
                {guardando ? '⏳...' : '💾 Guardar borrador'}
              </button>
              <button onClick={() => handleGuardar(true)} disabled={guardando}
                style={{ padding:'11px 28px', border:'none', borderRadius:10, background:'linear-gradient(135deg,#1a1a2e,#16213e)', color:'white', cursor:'pointer', fontSize:14, fontWeight:700 }}>
                {guardando ? '⏳...' : '✅ Enviar y bloquear'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Tab Compras directas — solo PDV */}
      {vistaTab === 'compras' && esPDV && (
        <ComprasDirectas
          bodegaId={bodegaId}
          fecha={fecha}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// COMPRAS DIRECTAS
// ─────────────────────────────────────────
function ComprasDirectas({ bodegaId, fecha }) {
  const [items, setItems] = useState([]);
  const [compras, setCompras] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [nueva, setNueva] = useState({ item_id: '', cantidad: '', valor_unitario: '', proveedor: '', notas: '' });

  useEffect(() => {
    API.get('/inventario/items', { params: { tipo: 'semanal' } }).then(r => setItems(r.data)).catch(() => {});
    if (bodegaId && fecha) {
      API.get('/inventario/compras', { params: { bodega_id: bodegaId, fecha } }).then(r => setCompras(r.data)).catch(() => {});
    }
  }, [bodegaId, fecha]);

  const cargarCompras = () => {
    if (bodegaId && fecha) {
      API.get('/inventario/compras', { params: { bodega_id: bodegaId, fecha } }).then(r => setCompras(r.data)).catch(() => {});
    }
  };

  const handleAgregar = async () => {
    if (!nueva.item_id || !nueva.cantidad) { return; }
    setGuardando(true);
    try {
      await API.post('/inventario/compras', {
        bodega_id: bodegaId,
        fecha,
        item_id: nueva.item_id,
        cantidad: parseFloat(nueva.cantidad),
        valor_unitario: parseFloat(nueva.valor_unitario) || 0,
        proveedor: nueva.proveedor,
        notas: nueva.notas,
      });
      cargarCompras();
      setNueva({ item_id: '', cantidad: '', valor_unitario: '', proveedor: '', notas: '' });
    } catch { }
    finally { setGuardando(false); }
  };

  const itemActual = items.find(i => String(i.id) === String(nueva.item_id));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Formulario nueva compra */}
      <div style={{ background:'white', borderRadius:14, padding:'20px 24px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ fontWeight:800, fontSize:15, marginBottom:16, color:'#111827' }}>🛒 Registrar compra directa</div>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12, marginBottom:12 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Ítem *</div>
            <select value={nueva.item_id} onChange={e => setNueva(p => ({...p, item_id: e.target.value}))}
              style={{ width:'100%', padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
              <option value="">Seleccionar...</option>
              {items.map(i => <option key={i.id} value={i.id}>{i.nombre} ({i.unidad_medida})</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Cantidad *</div>
            <input type="number" step="0.01" value={nueva.cantidad} onChange={e => setNueva(p => ({...p, cantidad: e.target.value}))}
              placeholder="0"
              style={{ width:'100%', padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Valor unitario</div>
            <input type="number" value={nueva.valor_unitario} onChange={e => setNueva(p => ({...p, valor_unitario: e.target.value}))}
              placeholder="0"
              style={{ width:'100%', padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12, marginBottom:16 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Proveedor</div>
            <input value={nueva.proveedor} onChange={e => setNueva(p => ({...p, proveedor: e.target.value}))}
              placeholder="Ej: Éxito, Mercado local..."
              style={{ width:'100%', padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Notas</div>
            <input value={nueva.notas} onChange={e => setNueva(p => ({...p, notas: e.target.value}))}
              placeholder="Observaciones..."
              style={{ width:'100%', padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
        </div>
        <button onClick={handleAgregar} disabled={guardando || !nueva.item_id || !nueva.cantidad}
          style={{ padding:'10px 24px', border:'none', borderRadius:10, background: (!nueva.item_id || !nueva.cantidad) ? '#E5E7EB' : 'linear-gradient(135deg,#1a1a2e,#3B4E8C)', color: (!nueva.item_id || !nueva.cantidad) ? '#9CA3AF' : 'white', cursor: (!nueva.item_id || !nueva.cantidad) ? 'not-allowed' : 'pointer', fontSize:14, fontWeight:700 }}>
          {guardando ? '⏳ Guardando...' : '+ Agregar compra'}
        </button>
      </div>

      {/* Lista de compras del día */}
      <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', fontWeight:800, fontSize:15 }}>📋 Compras registradas hoy</div>
        {compras.length === 0 ? (
          <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Sin compras registradas para esta fecha</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#F9FAFB' }}>
                {['Ítem','Cantidad','Valor unit.','Proveedor','Notas'].map(h => (
                  <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {compras.map((c, i) => (
                <tr key={c.id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                  <td style={{ padding:'10px 14px', fontWeight:600 }}>{c.item_nombre}</td>
                  <td style={{ padding:'10px 14px', fontFamily:'monospace', fontWeight:700, color:'#10B981' }}>{c.cantidad} {c.unidad}</td>
                  <td style={{ padding:'10px 14px', color:'#6B7280' }}>{c.valor_unitario > 0 ? new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(c.valor_unitario) : '—'}</td>
                  <td style={{ padding:'10px 14px', color:'#374151' }}>{c.proveedor || '—'}</td>
                  <td style={{ padding:'10px 14px', color:'#6B7280', fontSize:12 }}>{c.notas || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
